import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationDispatch, loadEmailNotifications } from '../../../store/application/types';
import { AppState } from '../../../store/rootReducer';
import { ColumnInfo, GenericGrid, LoadingPanel } from '@amsanalytics/react-shared-library';
import { GridFilterCellProps, GridCellProps, GridRowProps } from '@progress/kendo-react-grid';
import { Window } from '@progress/kendo-react-dialogs';
import { createStyles, Theme } from '@mui/material/styles';
import { SvgIcon } from '@progress/kendo-react-common';
import { filePdfIcon } from '@progress/kendo-svg-icons';
import { Button } from '@mui/material';
import { sendEmailNotification } from '../../../Data/SendEmailNotification';
import { fetchFile } from '../../../Data/fetch';
import { HtmlTooltip } from '../../Container/Dashboard/Dashboard';
import { ExpansionPanel, ExpansionPanelActionEvent, ExpansionPanelContent } from '@progress/kendo-react-layout';
import { Reveal } from '@progress/kendo-react-animation';
import { ImageViewerDialogData } from '../../IndirectStipsEditor/IndirectStipsEditor';

// const useStyles: any = makeStyles((theme: Theme) =>
//   createStyles({
//     formControl: {
//       margin: theme.spacing(1),
//       minWidth: 120,
//     },
//     selectEmpty: {
//       marginTop: theme.spacing(2),
//     },
//     textField: {
//       marginLeft: theme.spacing(1),
//       marginRight: theme.spacing(1),
//       width: 200,
//     },
//     visuallyHidden: {
//       border: 0,
//       clip: 'rect(0 0 0 0)',
//       height: 1,
//       margin: -1,
//       overflow: 'hidden',
//       padding: 0,
//       position: 'absolute',
//       top: 20,
//       width: 1,
//     },
//     hover: {
//       height: `60px`, // Ensure row height is consistent
//       '&:hover': {
//         cursor: 'pointer',
//       },
//     },
//     gridTitle: {
//       display: 'flex',
//       alignItems: 'center',
//       justifyContent: 'space-between',
//       flexWrap: 'wrap',
//       height: '50px',
//       color: '#3f51b5',
//       margin: 0,
//     },
//     gridTitleText: {
//       fontSize: 20,
//       fontWeight: 500,
//     },
//     itemName: {
//       fontWeight: 500,
//       marginRight: '15px',
//     },
    // documentImages: {
    //   display: 'flex',
    //   flexWrap: 'wrap',
    //   alignItems: 'center',
    // },
    // documentIcon: {
    //   display: 'flex',
    //   alignItems: 'center',
    // },
//   })
// );

export interface EmailNotificationProps {
  onSelect?: (appId: string) => void;
}

interface IEmailNotificationAttachment {
  documentId: string;
  documentName: string;
  mimeType: string;
}

export interface IEmailNotificationItems {
  id: string;
  toAddress: string;
  fromAddress: string;
  subject: string;
  body: string;
  successfullySent: number;
  messageId: string;
  attachments: IEmailNotificationAttachment[];
  createdDate?: Date;
}

export interface IEmailOutbound {
  emailApplicationActivityId: string;
  toAddress: string;
  fromAddress: string;
  subject: string;
  body: string;
}

interface ApplicationQueueGridColumn extends ColumnInfo {
  filtercell?: React.ComponentType<GridFilterCellProps>;
  filterTitle?: string;
}

const CreatedDateCell = (props: GridCellProps) => {
  return (
    <td>
      {new Date(props.dataItem.createdDate).toLocaleString([], {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })}
    </td>
  );
};

const ResendEmail = (props: GridCellProps & { onClick(item: IEmailNotificationItems): void }) => {
  return (
    <td>
      <Button onClick={() => props.onClick(props.dataItem)}>Resend</Button>
    </td>
  );
};

const SendEmailMessage = async (
  dispatch: ApplicationDispatch,
  emailApplicationActivityId: string,
  toAddress: string,
  fromAddress: string,
  subject: string,
  body: string
) => {
  const emailMessage: IEmailOutbound = { emailApplicationActivityId, toAddress, fromAddress, subject, body };
  await sendEmailNotification(emailMessage, dispatch);
};

export default function JdPowersportHistory(props: EmailNotificationProps) {
  const dispatch = useDispatch();
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const emailNotificationItems = useSelector((state: AppState) => state.applications.emailNotificationItems);
  // List of DocumentId/ObjectUrl pairs for each document. use a ref so we do not lose data across re-renders
  const documentUrls = useRef<{ documentId: string; objectUrl: string | undefined; isLoading: boolean }[]>(
    emailNotificationItems
      .filter((item) => item.attachments.length > 0)
      .flatMap((item) =>
        item.attachments.map((attachment) => ({
          documentId: attachment.documentId,
          objectUrl: undefined,
          isLoading: true,
        }))
      )
  );
  // whether or not to show the image viewer dialog (for previewing images in full size)
  const [showImageViewerDialog, setShowImageViewerDialog] = useState<ImageViewerDialogData>({ open: false });
  // whether or not to show the pdf viewer dialog (for previewing PDFs in full size)
  const [showPdfViewerDialog, setShowPdfViewerDialog] = useState<ImageViewerDialogData>({ open: false });
  // List of rows for which the body accordion is expanded
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  // returns object URL for the file
  const downloadFile = async (documentId: string, dispatch: any) => {
    const url = `api/applicationdocument/download/${documentId}`;
    const response = await fetchFile(url, dispatch);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return window.URL.createObjectURL(blob);
  };
  useEffect(() => {
    if (currentApp) {
      dispatch(loadEmailNotifications(currentApp.id!));
    }
  }, [currentApp]);

  // When list of emailNotificationItems changes, download the file and store the URL in the documentUrls array.
  // do not lost data across re-renders
  useEffect(() => {
    if (emailNotificationItems.some((item) => item.attachments.length > 0)) {
      emailNotificationItems
        .filter((item) => item.attachments.length > 0)
        .forEach((item) => {
          item.attachments.forEach((attachment) => {
            downloadFile(attachment.documentId, dispatch).then((url: string | null) => {
              if (url) {
                const index = documentUrls.current.findIndex((doc) => doc.documentId === attachment.documentId);
                if (index !== -1) {
                  documentUrls.current[index] = {
                    documentId: attachment.documentId,
                    objectUrl: url || undefined,
                    isLoading: false,
                  };
                } else {
                  documentUrls.current.push({ documentId: attachment.documentId, objectUrl: url || undefined, isLoading: false });
                }
              }
            });
          });
        });
    }
  }, [dispatch, emailNotificationItems]);

  const DocumentPdfIcon = (props: { documentName: string; documentUrl: string }) => {
    return (
      <div>
        <SvgIcon
          icon={filePdfIcon}
          onClick={() =>
            setShowPdfViewerDialog({
              documentName: props.documentName,
              objectUrl: props.documentUrl || '',
              open: true,
            })
          }
          height={30}
          color='red'
          style={{ cursor: 'pointer' }}
        />
      </div>
    );
  };
  const DocumentIcon = (props: { documentName: string; documentUrl: string }) => {
    return (
      <div style={{ display: 'flex', flex: 0, alignItems: 'center' }}>
        <img
          src={props.documentUrl}
          alt='Notification attachment'
          onClick={() =>
            setShowImageViewerDialog({
              documentName: props.documentName,
              objectUrl: props.documentUrl || '',
              open: true,
            })
          }
          height={40}
          style={{ cursor: 'pointer' }}
        />
      </div>
    );
  };

  // List of columns for the generic grid
  const genericGridColumns: ApplicationQueueGridColumn[] = [];
  genericGridColumns.push({
    title: 'Created Date',
    field: 'createdDate',
    cell: CreatedDateCell,
    format: '{0:MMMM d, yyyy h:mm a}',
  });
  genericGridColumns.push({ title: 'To Address', field: 'toAddress' });
  genericGridColumns.push({ title: 'From Address', field: 'fromAddress' });
  genericGridColumns.push({ title: 'Subject', field: 'subject' });
  genericGridColumns.push({
    title: '',
    cell: (props: any) => (
      <ResendEmail
        {...props}
        onClick={(item: IEmailNotificationItems) => {
          SendEmailMessage(dispatch, item.id, item.toAddress, item.fromAddress, item.subject, item.body);
        }}
      />
    ),
  });

  // returns the dialog title, which is the document name. truncates the document name if it is too long (20 characters)
  const getDialogTitle = (documentName: string | undefined): string => {
    return documentName ? (documentName.length > 20 ? documentName.substring(0, 20) + '...' : documentName) : '';
  };

  const rowRender = (
    row: React.ReactElement<HTMLTableRowElement, string | React.JSXElementConstructor<any>>,
    props: GridRowProps
  ) => {
    return (
      <React.Fragment>
        {row}
        <td colSpan={5}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                margin: '5px'
              }}
            >
              {props.dataItem.attachments.map((attachment: IEmailNotificationAttachment) => {
                const documentUrlItem = documentUrls.current.find(
                  (du) => du.documentId.toUpperCase() === attachment.documentId.toUpperCase()
                );
                return (
                  <HtmlTooltip
                    arrow
                    title={
                      <React.Fragment>
                        <p style={{ margin: 0, padding: 0 }}>
                          <strong>{attachment.documentName}</strong>
                        </p>
                      </React.Fragment>
                    }
                  >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {documentUrlItem?.isLoading && <LoadingPanel label='' style={{ height: 30, width: 30 }} />}
                      {attachment.mimeType !== 'application/pdf' && documentUrlItem?.objectUrl && (
                        <DocumentIcon documentUrl={documentUrlItem?.objectUrl} documentName={attachment.documentName} />
                      )}
                      {attachment.mimeType === 'application/pdf' && documentUrlItem?.objectUrl && (
                        <DocumentPdfIcon documentUrl={documentUrlItem?.objectUrl} documentName={attachment.documentName} />
                      )}
                    </div>
                  </HtmlTooltip>
                );
              })}
            </div>
            <ExpansionPanel
              title={expandedRows.includes(props.dataItem.id) ? 'Hide Message' : 'Show Message'}
              expanded={expandedRows.includes(props.dataItem.id)}
              onAction={(event: ExpansionPanelActionEvent) => {
                event.expanded
                  ? setExpandedRows(expandedRows.filter((id) => id !== props.dataItem.id))
                  : setExpandedRows([...expandedRows, props.dataItem.id]);
              }}
              style={{ width: '100%' }}
            >
              <Reveal>
                {expandedRows.includes(props.dataItem.id) && (
                  <ExpansionPanelContent>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: props.dataItem.body,
                      }}
                    />
                  </ExpansionPanelContent>
                )}
              </Reveal>
            </ExpansionPanel>
          </div>
        </td>
      </React.Fragment>
    );
  };

  return (
    <>
      <GenericGrid<IEmailNotificationItems>
        columns={genericGridColumns}
        rowRender={rowRender}
        items={
          emailNotificationItems?.map((eni) => {
            return {
              ...eni,
              createdDate: eni.createdDate ? new Date(eni.createdDate) : undefined,
            };
          }) || []
        }
        itemName={'Email Notifications'}
        totalCount={emailNotificationItems?.length || 0}
        pageSizes={[10, 20, 40]}
        style={{ height: 'calc(100vh - 210px)' }}
        disableAdding={true}
        disableEditing={true}
        disableSorting={true}
        disableGrouping={true}
      />
      {showImageViewerDialog.open && (
        <Window
          title={getDialogTitle(showImageViewerDialog.documentName)}
          onClose={() => setShowImageViewerDialog({ documentName: undefined, objectUrl: undefined, open: false })}
          height={500}
          width={500}
        >
          <img
            src={showImageViewerDialog.objectUrl}
            alt={showImageViewerDialog.documentName}
            style={{ width: '100%', height: '100%', overflow: 'auto' }}
          />
        </Window>
      )}
      {showPdfViewerDialog.open && (
        <Window
          title={getDialogTitle(showPdfViewerDialog.documentName)}
          onClose={() => setShowPdfViewerDialog({ documentName: undefined, objectUrl: undefined, open: false })}
          stage='FULLSCREEN'
        >
          <iframe
            src={showPdfViewerDialog.objectUrl}
            title={showPdfViewerDialog.documentName}
            style={{ width: '100%', height: '100%' }}
          />
        </Window>
      )}
    </>
  );
}
